import { app, authentication } from '@microsoft/teams-js';

/**
 * Único arquivo do frontend que fala com o Teams (o Biome bloqueia o
 * `@microsoft/teams-js` em qualquer outro). O resto da aplicação recebe um
 * `TeamsHost` ou `null` e não sabe nada do SDK.
 */

export type TeamsTheme = 'light' | 'dark' | 'contrast';

export interface TeamsHost {
  readonly theme: TeamsTheme;
  /** Avisa quando a pessoa troca o tema do Teams. Devolve o cancelamento. */
  onThemeChange(listener: (theme: TeamsTheme) => void): () => void;
  /** Token SSO da pessoa para a API desta aplicação (escopo access_as_user). */
  getAuthToken(): Promise<string>;
  /**
   * Quem o Teams diz que está usando, lido do contexto. Não é autenticado
   * (qualquer página pode inventar um contexto): serve só para o modo dev,
   * que dispensa o SSO no F5.
   */
  readonly user: TeamsUser | null;
  /** O contexto que o Teams entregou, para a página Teams > Usuário. */
  readonly context: TeamsContextSnapshot;
}

/**
 * O contexto do Teams (`app.getContext()`), que vem sem pedir permissão
 * nenhuma: os campos mais úteis já separados e o objeto inteiro (`raw`),
 * copiado como dado puro. Campo vazio vira `null`.
 */
export interface TeamsContextSnapshot {
  readonly user: {
    readonly id: string | null;
    readonly displayName: string | null;
    readonly userPrincipalName: string | null;
    readonly loginHint: string | null;
    readonly tenantId: string | null;
    readonly licenseType: string | null;
  };
  readonly app: {
    readonly locale: string | null;
    readonly theme: string | null;
    readonly sessionId: string | null;
    readonly hostName: string | null;
    readonly clientType: string | null;
    readonly ringId: string | null;
  };
  readonly page: {
    readonly id: string | null;
    readonly frameContext: string | null;
    readonly subPageId: string | null;
  };
  readonly raw: unknown;
}

export interface TeamsUser {
  readonly email: string;
  readonly name: string;
}

/** O Teams pode mandar campos vazios em vez de omiti-los. */
const filled = (value: string | undefined): string | undefined =>
  value?.trim() || undefined;

function userOf(context: app.Context): TeamsUser | null {
  const email =
    filled(context.user?.userPrincipalName) ?? filled(context.user?.loginHint);
  if (email === undefined) {
    return null;
  }
  return { email, name: filled(context.user?.displayName) ?? email };
}

const orNull = (value: string | undefined): string | null =>
  filled(value) ?? null;

function snapshotOf(context: app.Context): TeamsContextSnapshot {
  return {
    user: {
      id: orNull(context.user?.id),
      displayName: orNull(context.user?.displayName),
      userPrincipalName: orNull(context.user?.userPrincipalName),
      loginHint: orNull(context.user?.loginHint),
      tenantId: orNull(context.user?.tenant?.id),
      licenseType: orNull(context.user?.licenseType),
    },
    app: {
      locale: orNull(context.app.locale),
      theme: orNull(context.app.theme),
      sessionId: orNull(context.app.sessionId),
      hostName: orNull(context.app.host.name),
      clientType: orNull(context.app.host.clientType),
      ringId: orNull(context.app.host.ringId),
    },
    page: {
      id: orNull(context.page.id),
      frameContext: orNull(context.page.frameContext),
      subPageId: orNull(context.page.subPageId),
    },
    // Só dados: o SDK não põe funções no contexto, mas a cópia garante.
    raw: JSON.parse(JSON.stringify(context)),
  };
}

/**
 * Fora do Teams o `app.initialize()` não falha: fica esperando um host que
 * nunca responde. O limite evita que o navegador fique preso na tela inicial.
 */
const INITIALIZE_TIMEOUT_MS = 3000;

const toTheme = (value: string | undefined): TeamsTheme =>
  value === 'dark' || value === 'contrast' ? value : 'light';

/** Dentro do Teams a aplicação roda num iframe ou num webview do app móvel. */
function mightBeInTeams(): boolean {
  return window.self !== window.top || 'nativeInterface' in window;
}

async function initialize(): Promise<boolean> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<false>((resolve) => {
    timer = setTimeout(() => resolve(false), INITIALIZE_TIMEOUT_MS);
  });
  try {
    return await Promise.race([app.initialize().then(() => true), timeout]);
  } catch {
    return false;
  } finally {
    clearTimeout(timer);
  }
}

async function connect(): Promise<TeamsHost | null> {
  if (!(mightBeInTeams() && (await initialize()))) {
    return null;
  }
  const context = await app.getContext();
  const listeners = new Set<(theme: TeamsTheme) => void>();
  app.registerOnThemeChangeHandler((theme) => {
    for (const listener of listeners) {
      listener(toTheme(theme));
    }
  });
  // Tira a tela de carregamento do Teams assim que o app está pronto.
  app.notifySuccess();
  return {
    theme: toTheme(context.app.theme),
    onThemeChange(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getAuthToken: () => authentication.getAuthToken(),
    user: userOf(context),
    context: snapshotOf(context),
  };
}

let connection: Promise<TeamsHost | null> | undefined;

/** Conecta uma vez só; as chamadas seguintes reaproveitam o resultado. */
export function connectToTeams(): Promise<TeamsHost | null> {
  connection ??= connect();
  return connection;
}
